/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
// react router
import { useHistory } from "react-router-dom";
// redux
import { useSelector, useDispatch } from "react-redux";
// nodejs library that concatenates classes
import classnames from "classnames";
// react plugin for creating notifications over the dashboard
import NotificationAlert from "react-notification-alert";
// reactstrap components
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Row,
  UncontrolledTooltip,
  Col,
} from "reactstrap";
// Firebase
import firebase from "firebase";

// core components
import AuthHeader from "components/Headers/AuthHeader.js";
import Loading from "components/Misc/Loading";

function Register() {
  const history = useHistory();
  const userState = useSelector(state => state.user);
  const dispatch = useDispatch();
  // franchises state
  // Focus state
  const [focusedEmail, setfocusedEmail] = React.useState(false);
  const [focusedPassword, setfocusedPassword] = React.useState(false);
  const [
    focusedPasswordConfirmation,
    setfocusedPasswordConfirmation,
  ] = React.useState(false);
  //Content State
  const [emailState, setemailState] = React.useState("");
  const [passwordState, setpasswordState] = React.useState("");
  const [
    passwordConfirmationState,
    setpasswordConfirmationState,
  ] = React.useState("");
  const [checkboxState, setCheckboxState] = React.useState(false);
  //Validation State
  const [validEmailState, setValidEmailState] = React.useState(null);
  const [validPasswordState, setValidPasswordState] = React.useState(null);
  const [
    validPasswordConfirmationState,
    setValidPasswordConfirmationState,
  ] = React.useState(null);
  const [validCheckboxState, setValidCheckboxState] = React.useState(null);

  const [passwordStrength, setPasswordStrength] = React.useState("success");
  const [loadingActive, setLoadingActive] = React.useState(false);

  const strengthRef = React.useRef();
  const passwordStrengthTextRef = React.useRef("Low");
  const notificationAlertRef = React.useRef(null);

  const analyzePassword = e => {
    setpasswordState(e.target.value);
    if (e.target.value.length === 0) {
      return (strengthRef.current.style.display = "none");
    }
    const strongPassword = new RegExp(
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})"
    );
    const mediumPassword = new RegExp(
      "^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})"
    );
    if (strongPassword.test(e.target.value)) {
      strengthRef.current.style.display = "block";
      passwordStrengthTextRef.current = "Strong";
      setValidPasswordState("valid");
      return setPasswordStrength("success");
    }
    if (mediumPassword.test(e.target.value)) {
      strengthRef.current.style.display = "block";
      passwordStrengthTextRef.current = "Medium";
      setValidPasswordState("valid");
      return setPasswordStrength("yellow");
    }
    strengthRef.current.style.display = "block";
    passwordStrengthTextRef.current = "Low";
    setPasswordStrength("danger");
    setValidPasswordState("invalid");
  };

  const notify = (type, title, message, icon) => {
    let options = {
      place: "tc",
      message: (
        <div className="alert-text">
          <span className="alert-title" data-notify="title">
            {" "}
            {title}
          </span>
          <span data-notify="message">{message}</span>
        </div>
      ),
      type: type,
      icon,
      autoDismiss: 7,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoadingActive(true);
    try {
      if (
        validEmailState === "valid" &&
        validPasswordState === "valid" &&
        validPasswordConfirmationState === "valid" &&
        validCheckboxState === "valid"
      ) {
        if (!userState.user) {
          await firebase
            .auth()
            .createUserWithEmailAndPassword(emailState, passwordState);
        }
        setLoadingActive(false);
      }
      if (!validEmailState || validEmailState === "invalid") {
        setValidEmailState("invalid");
        setLoadingActive(false);
      }
      if (!validPasswordState || validPasswordState === "invalid") {
        setValidPasswordState("invalid");
        setLoadingActive(false);
      }
      if (
        !validPasswordConfirmationState ||
        validPasswordConfirmationState === "invalid"
      ) {
        setValidPasswordConfirmationState("invalid");
        setLoadingActive(false);
      }
      if (!validCheckboxState || validCheckboxState === "invalid") {
        setValidCheckboxState("invalid");
        setLoadingActive(false);
      }
    } catch (error) {
      notify("danger", "Uh Oh!", error.message, "fas fa-exclamation-triangle");
      setLoadingActive(false);
      console.error(error);
    }
  };

  return (
    <>
      <div className="rna-wrapper">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Loading active={loadingActive} />
      <AuthHeader
        title="Create an account"
        lead="Please fill out this fields to create your account."
      />
      <Container className="mt--8 pb-5">
        <Row className="justify-content-center">
          <Col lg="6" md="8">
            <Card className="bg-secondary border-0">
              <CardBody className="px-lg-5 py-lg-5">
                <Form
                  role="form"
                  onSubmit={e => handleSubmit(e)}
                  id="register"
                  className="needs-validation"
                  noValidate
                >
                  <FormGroup
                    className={classnames({
                      focused: focusedEmail,
                    })}
                  >
                    <InputGroup className="input-group-merge mb-3">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText
                          style={
                            validEmailState === "valid"
                              ? { borderColor: "#2dce89" }
                              : validEmailState === "invalid"
                              ? { borderColor: "red" }
                              : {}
                          }
                        >
                          <i className="ni ni-email-83" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="Email"
                        type="email"
                        valid={validEmailState === "valid"}
                        invalid={validEmailState === "invalid"}
                        onFocus={() => setfocusedEmail(true)}
                        onBlur={() => setfocusedEmail(false)}
                        onChange={e => {
                          setemailState(e.target.value);
                          if (
                            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                              e.target.value
                            )
                          ) {
                            setValidEmailState("valid");
                          } else {
                            setValidEmailState("invalid");
                          }
                        }}
                      />
                    </InputGroup>
                  </FormGroup>
                  <FormGroup
                    className={classnames({
                      focused: focusedPassword,
                    })}
                  >
                    <InputGroup className="input-group-merge">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText
                          style={
                            validPasswordState === "valid"
                              ? { borderColor: "#2dce89" }
                              : validPasswordState === "invalid"
                              ? { borderColor: "red" }
                              : {}
                          }
                        >
                          <i className="ni ni-lock-circle-open" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="Password"
                        type="password"
                        valid={validPasswordState === "valid"}
                        invalid={validPasswordState === "invalid"}
                        onFocus={() => setfocusedPassword(true)}
                        onBlur={() => setfocusedPassword(false)}
                        onChange={e => analyzePassword(e)}
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText
                          style={
                            validPasswordState === "valid"
                              ? { borderColor: "#2dce89" }
                              : validPasswordState === "invalid"
                              ? { borderColor: "red" }
                              : {}
                          }
                        >
                          <i className="fas fa-info-circle" id="password" />
                          <UncontrolledTooltip
                            delay={0}
                            placement="top"
                            target="password"
                          >
                            Password should be at least 6 characters long, with
                            at least one capital letter, one lower case letter,
                            one number and/or one special character
                          </UncontrolledTooltip>
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </FormGroup>
                  <div
                    className="text-muted font-italic"
                    ref={strengthRef}
                    style={{ display: "none" }}
                  >
                    <small>
                      password strength:{" "}
                      <span
                        className={`text-${passwordStrength} font-weight-700`}
                      >
                        {passwordStrengthTextRef.current}
                      </span>
                    </small>
                  </div>
                  <FormGroup
                    className={
                      "mt-4 " +
                      classnames({
                        focused: focusedPasswordConfirmation,
                      })
                    }
                  >
                    <InputGroup className="input-group-merge">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText
                          style={
                            validPasswordConfirmationState === "valid"
                              ? { borderColor: "#2dce89" }
                              : validPasswordConfirmationState === "invalid"
                              ? { borderColor: "red" }
                              : {}
                          }
                        >
                          <i className="ni ni-lock-circle-open" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        placeholder="Confirm Password"
                        type="password"
                        valid={validPasswordConfirmationState === "valid"}
                        invalid={validPasswordConfirmationState === "invalid"}
                        onFocus={() => setfocusedPasswordConfirmation(true)}
                        onBlur={() => setfocusedPasswordConfirmation(false)}
                        onChange={e => {
                          setpasswordConfirmationState(e.target.value);
                          if (e.target.value === passwordState) {
                            setValidPasswordConfirmationState("valid");
                          } else {
                            setValidPasswordConfirmationState("invalid");
                          }
                        }}
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText
                          style={
                            validPasswordConfirmationState === "valid"
                              ? { borderColor: "#2dce89" }
                              : validPasswordConfirmationState === "invalid"
                              ? { borderColor: "red" }
                              : {}
                          }
                        >
                          <i
                            className="fas fa-info-circle"
                            id="password-confirmation"
                          />
                          <UncontrolledTooltip
                            delay={0}
                            placement="top"
                            target="password-confirmation"
                          >
                            Please make sure this password matches the one
                            above.
                          </UncontrolledTooltip>
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </FormGroup>
                  <Row className="my-4">
                    <Col xs="12">
                      <div className="custom-control custom-control-alternative custom-checkbox">
                        <input
                          className={
                            (validCheckboxState === "valid"
                              ? "is-valid "
                              : validCheckboxState === "invalid"
                              ? "is-invalid "
                              : "") + "custom-control-input"
                          }
                          id="customCheckRegister"
                          type="checkbox"
                          onChange={e => {
                            setCheckboxState(e.target.checked);
                            if (e.target.checked === false) {
                              setValidCheckboxState("invalid");
                            } else {
                              setValidCheckboxState("valid");
                            }
                          }}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="customCheckRegister"
                        >
                          <span className="text-muted">
                            I agree with the{" "}
                            <a href="#" onClick={e => history.push('/auth/privacy-policy')}>
                              Privacy Policy
                            </a>
                          </span>
                        </label>
                        {validCheckboxState === "invalid" ? (
                          <div className="invalid-feedback">
                            You must agree before submitting.
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <div className="text-center">
                    <Button
                      className="mt-4"
                      color="info"
                      type="button"
                      type="submit"
                      form="register"
                    >
                      Create account
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row
          className="justify-content-center"
          style={{ marginTop: "-0.6rem" }}
        >
          <Col xs="6">
            <a
              className="text-light"
              href="#pablo"
              onClick={() => history.push("/login")}
            >
              <small>Back to Login</small>
            </a>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Register;
